<!-- ═══════════════════════════════════════════════════════════════════════
     FORECAST — Vista analítica de indicadores de cobranza y ventas
     Layout fijo CSS Grid, sin gridster
     ═══════════════════════════════════════════════════════════════════════ -->
<div class="min-h-screen bg-slate-50 -m-4 sm:-m-8">

  <!-- ── Page header ─────────────────────────────────────────────────────── -->
  <div class="bg-white border-b border-slate-200 px-6 py-5">
    <div class="flex flex-wrap items-start justify-between gap-4">

      <!-- Title -->
      <div>
        <div class="flex items-center gap-2">
          <span class="flex items-center justify-center w-9 h-9 rounded-xl bg-indigo-600">
            <i class="pi pi-chart-line text-white text-base"></i>
          </span>
          <h1 class="text-2xl font-bold text-slate-900">Forecast</h1>
        </div>
        <p class="mt-1 text-sm text-slate-500 ml-11">{{ todayLabel() }}</p>
      </div>

      <!-- Controls -->
      <div class="flex flex-wrap items-center gap-3">
        <!-- Period range text -->
        <span class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium">
          <i class="pi pi-calendar text-slate-400"></i>
          {{ periodLabel() }}
        </span>

        <!-- Period buttons -->
        <div class="flex rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
          @for (opt of periodOptions; track opt.value) {
            <button type="button" (click)="setPeriod(opt.value)"
              class="px-3 py-2 text-xs font-semibold transition-colors"
              [class.bg-indigo-600]="period().type === opt.value && opt.value !== 'custom'"
              [class.text-white]="period().type === opt.value && opt.value !== 'custom'"
              [class.text-slate-600]="period().type !== opt.value"
              [class.hover:bg-slate-50]="period().type !== opt.value">
              {{ opt.label }}
            </button>
          }
        </div>

        <!-- Refresh -->
        <button type="button" (click)="load()" [disabled]="data()?.loading"
          class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 text-xs font-medium disabled:opacity-50 shadow-sm">
          <i class="pi pi-refresh text-sm" [class.animate-spin]="data()?.loading"></i>
          Actualizar
        </button>

        <!-- New invoice CTA -->
        <a [routerLink]="['/invoices/nuevo']"
          class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors shadow-sm">
          <i class="pi pi-plus"></i>
          Nueva factura
        </a>
      </div>
    </div>

    <!-- Custom date range -->
    @if (period().type === 'custom') {
      <div class="mt-4 flex flex-wrap items-center gap-3 ml-11">
        <input type="date" [value]="customDateFrom()"
          (input)="customDateFrom.set($any($event.target).value)"
          class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">
        <span class="text-slate-400 text-sm">—</span>
        <input type="date" [value]="customDateTo()"
          (input)="customDateTo.set($any($event.target).value)"
          class="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">
        <button type="button" (click)="applyCustomPeriod()"
          [disabled]="!customDateFrom() || !customDateTo()"
          class="px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
          Aplicar
        </button>
      </div>
    }
  </div>

  <!-- ── Summary strip ────────────────────────────────────────────────────── -->
  @if (summaryLine(); as s) {
    <div class="bg-indigo-600 text-white px-6 py-2.5 flex flex-wrap items-center gap-6 text-sm">
      <span class="flex items-center gap-2">
        <i class="pi pi-file-edit opacity-70 text-xs"></i>
        <span class="opacity-75">Facturado</span>
        <strong class="tabular-nums">{{ formatCurrency(s.issued) }}</strong>
      </span>
      <span class="opacity-30 hidden sm:inline">|</span>
      <span class="flex items-center gap-2">
        <i class="pi pi-check-circle opacity-70 text-xs"></i>
        <span class="opacity-75">Cobrado</span>
        <strong class="tabular-nums text-emerald-300">{{ formatCurrency(s.paid) }}</strong>
      </span>
      <span class="opacity-30 hidden sm:inline">|</span>
      <span class="flex items-center gap-2">
        <i class="pi pi-clock opacity-70 text-xs"></i>
        <span class="opacity-75">Pendiente</span>
        <strong class="tabular-nums text-amber-300">{{ formatCurrency(s.pending) }}</strong>
      </span>
      <span class="opacity-30 hidden sm:inline">|</span>
      <span class="flex items-center gap-2">
        <i class="pi pi-percentage opacity-70 text-xs"></i>
        <span class="opacity-75">Tasa de cobranza</span>
        <strong class="tabular-nums">{{ s.rate }}%</strong>
      </span>
    </div>
  }

  <!-- ── Error ─────────────────────────────────────────────────────────────── -->
  @if (data()?.error) {
    <div class="mx-6 mt-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-amber-500"></i>
      {{ data()?.error }}
    </div>
  }

  <!-- ── Main content ──────────────────────────────────────────────────────── -->
  <div class="p-6 space-y-5">

    <!-- ── Fila 1: KPI Cards ──────────────────────────────────────────────── -->
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

      <!-- KPI Cobranza -->
      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
           style="height: 120px;">
        <div class="h-1 bg-emerald-500"></div>
        <div style="height: calc(100% - 4px);">
          <app-dash-kpi-collection [data]="data()" />
        </div>
      </div>

      <!-- KPI Proyección -->
      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
           style="height: 120px;">
        <div class="h-1 bg-indigo-500"></div>
        <div style="height: calc(100% - 4px);">
          <app-dash-kpi-projection [data]="data()" />
        </div>
      </div>

      <!-- KPI Pendiente -->
      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
           style="height: 120px;">
        <div class="h-1 bg-amber-500"></div>
        <div style="height: calc(100% - 4px);">
          <app-dash-kpi-pending [data]="data()" />
        </div>
      </div>

      <!-- KPI Tasa -->
      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
           style="height: 120px;">
        <div class="h-1 bg-violet-500"></div>
        <div style="height: calc(100% - 4px);">
          <app-dash-kpi-rate [data]="data()" />
        </div>
      </div>
    </div>

    <!-- ── Fila 2: Gráfica principal + Últimos pagos ──────────────────────── -->
    <div class="grid grid-cols-1 xl:grid-cols-12 gap-4">

      <!-- Ventas en el tiempo (8/12) -->
      <div class="xl:col-span-8 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
           style="height: 320px;">
        <app-dash-sales-chart [data]="data()" />
      </div>

      <!-- Últimos pagos (4/12) -->
      <div class="xl:col-span-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
           style="height: 320px;">
        <app-dash-recent-payments [data]="data()" />
      </div>
    </div>

    <!-- ── Fila 3: Timeline + Desglose ───────────────────────────────────── -->
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">

      <!-- Cobranza por día -->
      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
           style="height: 280px;">
        <app-dash-collection-timeline [data]="data()" />
      </div>

      <!-- Cobrado vs Pendiente (bar horizontal) -->
      <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
           style="height: 280px;">
        <app-dash-collection-breakdown [data]="data()" />
      </div>
    </div>

    <!-- ── Fila 4: Próximos/vencidos + Donut estado ───────────────────────── -->
    <div class="grid grid-cols-1 xl:grid-cols-12 gap-4">

      <!-- Próximos y vencidos (7/12) -->
      <div class="xl:col-span-7 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
           style="height: 300px;">
        <app-dash-upcoming-overdue [data]="data()" />
      </div>

      <!-- Facturas por estado — donut (5/12) -->
      <div class="xl:col-span-5 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
           style="height: 300px;">
        <app-dash-invoice-status [data]="data()" />
      </div>
    </div>

  </div>
</div>
